// src/utils/questionnaireQuestions/renderStep.tsx
/* ------------------------------------------------------------------ */
/* renderStep – selects & renders the current questionnaire “step”     */
/* ------------------------------------------------------------------ */
/**
 * Helper used by **DynamicQuestionnaireForm**.
 *
 * @param {Array<object>} steps              Output of `buildSteps()`
 * @param {number}        currentStep        Zero-based index of the step to render
 * @param {object}        formData           `{ [questionId]: answer }`
 * @param {Function}      handleChange       `onChange` handler for <input/>, <select/>, …
 * @param {Function}      goNext             Callback that advances to the next step
 * @param {Array<object>} filteredQuestions  Full question list **after** conditional filtering
 *
 * @returns {JSX.Element|null}
 *
 * **Step types**
 *  - `single` → `<QuestionStep>`      (one question)
 *  - `group`  → `<GroupedQuestionStep>` (multiple questions with the same `group` label)
 *  - `review` → `<ReviewStep>`        (read-only summary before submission)
 */
import GroupedQuestionStep from "../../components/form/GroupedQuestionStep";
import QuestionStep from "../../components/form/QuestionStep";
import ReviewStep from "../../components/form/ReviewStep";

/* ------------------------------------------------------------------ */
/* Render                                                             */
/* ------------------------------------------------------------------ */
export const renderStep = (
  steps,
  currentStep,
  formData,
  handleChange,
  goNext,
  filteredQuestions
) => {
  const step = steps[currentStep];

  switch (step.type) {
    case "single":
      return (
        <QuestionStep
          question={step.question}
          value={formData[step.question.id]}
          onChange={handleChange}
          autoFocus
          onEnter={goNext}
        />
      );

    case "group":
      return (
        <GroupedQuestionStep
          questions={step.questions}
          formData={formData}
          onChange={handleChange}
          onEnter={goNext}
        />
      );

    case "review":
      return <ReviewStep questions={filteredQuestions} formData={formData} />;

    default:
      return null;
  }
};
